import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Package, Image, MessageSquare, ShoppingBag, TrendingUp, DollarSign, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminApi } from '../../context/AdminContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0, gallery: 0, testimonials: 0,
    orders: 0, pending_orders: 0, contacts: 0, total_revenue: 0
  });

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const response = await adminApi.get('/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const statCards = [
    { title: 'Products', value: stats.products, icon: Package, color: 'bg-blue-500', link: '/admin/products' },
    { title: 'Gallery', value: stats.gallery, icon: Image, color: 'bg-green-500', link: '/admin/gallery' },
    { title: 'Reviews', value: stats.testimonials, icon: MessageSquare, color: 'bg-purple-500', link: '/admin/testimonials' },
    { title: 'Orders', value: stats.orders, icon: ShoppingBag, color: 'bg-red-500', link: '/admin/orders' },
    { title: 'Pending Orders', value: stats.pending_orders, icon: TrendingUp, color: 'bg-yellow-500', link: '/admin/orders' },
    { title: 'Revenue', value: `${stats.total_revenue.toFixed(3)} BHD`, icon: DollarSign, color: 'bg-emerald-500', link: '/admin/orders' },
  ];

  const quickActions = [
    { title: 'Manage Products', desc: 'Add, edit or delete products', icon: Package, path: '/admin/products' },
    { title: 'View Orders', desc: `${stats.pending_orders} pending order${stats.pending_orders !== 1 ? 's' : ''}`, icon: ShoppingBag, path: '/admin/orders' },
    { title: 'Gallery', desc: 'Upload and organize images', icon: Image, path: '/admin/gallery' },
    { title: 'Contact Messages', desc: `${stats.contacts} message${stats.contacts !== 1 ? 's' : ''}`, icon: Mail, path: '/admin/settings' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
        <p className="text-gray-600">Welcome back! Here's your store overview.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Link key={idx} to={stat.link}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-3 sm:p-4">
                  <div className={`${stat.color} p-2 rounded-lg w-fit mb-2`}>
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <p className="text-lg sm:text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.title}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quickActions.map((action, idx) => (
                <Link key={idx} to={action.path} className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{action.title}</div>
                      <div className="text-sm text-gray-600">{action.desc}</div>
                    </div>
                    <action.icon className="h-5 w-5 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>System Status</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { label: 'System is running smoothly', sub: 'All services operational', color: 'bg-green-500' },
                { label: 'Database connected', sub: `${stats.products} products, ${stats.orders} orders`, color: 'bg-blue-500' },
                { label: 'Bilingual support active', sub: 'English & Arabic', color: 'bg-purple-500' },
                { label: 'JWT Authentication', sub: 'Admin endpoints secured', color: 'bg-emerald-500' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 ${item.color} rounded-full mt-2`}></div>
                  <div>
                    <div className="text-sm font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
